import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { change } from 'redux-form';

import { INQUIRY_TEMPLATE_EDITOR_FORM_NAME } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import {
  ColorStyleInput,
  StyleInput,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/inputs';
import {
  SectionColumn,
  SectionRow,
} from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorStyleSections/sections/shared';
import { StepPreview } from 'lib/interfaces/StepsConfig';

type ScreenStylingProps = {
  basePath: string;
  stepPreview: StepPreview;
  showHeaderStyleOptions: boolean;
  editable: boolean;
};

const COLOR_DEFAULT = {
  value: '#00000000',
  unit: 'hex',
};

const BORDER_DEFAULT = {
  value: 0,
  unit: 'px',
};

const ScreenStylingSection = ({
  basePath,
  stepPreview,
  showHeaderStyleOptions,
  editable,
}: ScreenStylingProps) => {
  const dispatch = useDispatch();
  const updatePath = `steps.${basePath}.style-variants.0`;

  const screenBackgroundColor = stepPreview?.styles?.['background-color']?.base?.base ?? {
    value: '#00000000',
    unit: 'hex',
  };

  const headerStyleOptions = useMemo(() => {
    return {
      iconColor: stepPreview?.styles?.['text-color']?.['header-button']?.base ?? COLOR_DEFAULT,
      borderColor: stepPreview?.styles?.['border-color']?.header?.base ?? COLOR_DEFAULT,
      borderWidth: stepPreview?.styles?.['border-width']?.header?.base?.bottom ?? BORDER_DEFAULT,
    };
  }, [stepPreview]);

  /**
   * Note (Evan): When none of the elements that are within the nav header are visible
   * we need to reset bottom border.
   *
   * This is because within web inquiries, we still render the nav element, and without
   * the reset, the bottom border styling would still be visible
   */
  useEffect(() => {
    if (!showHeaderStyleOptions && headerStyleOptions.borderWidth.value !== 0) {
      dispatch(
        change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, `${updatePath}.border-width--header`, {
          bottom: BORDER_DEFAULT,
        }),
      );
    }
  }, [showHeaderStyleOptions, headerStyleOptions, dispatch, updatePath]);

  const onColorChange = useCallback(
    (value: string, field: string) => {
      dispatch(
        change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, `${updatePath}.${field}`, {
          value,
          unit: 'hex',
        }),
      );
    },
    [updatePath, dispatch],
  );

  const onBorderChange = useCallback(
    (value: string, field: string) => {
      dispatch(
        change(INQUIRY_TEMPLATE_EDITOR_FORM_NAME, `${updatePath}.${field}`, {
          bottom: {
            value: parseInt(value),
            unit: 'px',
          },
        }),
      );
    },
    [updatePath, dispatch],
  );

  return (
    <div>
      <SectionRow>
        <SectionColumn>
          <ColorStyleInput
            label='Background color'
            field='background-color'
            name='screen-background-color'
            value={screenBackgroundColor.value}
            editable={editable}
            onChange={onColorChange}
          />
        </SectionColumn>
      </SectionRow>
      {showHeaderStyleOptions && (
        <>
          <SectionRow>
            <SectionColumn>
              <ColorStyleInput
                label='Header button color'
                field='text-color--header-button'
                name='screen-header-icon-color'
                value={headerStyleOptions.iconColor.value}
                editable={editable}
                onChange={onColorChange}
              />
            </SectionColumn>
          </SectionRow>
          <SectionRow>
            <SectionColumn>
              <ColorStyleInput
                label='Header border color'
                field='border-color--header'
                name='screen-header-border-color'
                value={headerStyleOptions.borderColor.value}
                editable={editable}
                onChange={onColorChange}
              />
            </SectionColumn>
            <SectionColumn>
              <StyleInput
                label='Header border width'
                field='border-width--header'
                name='screen-config-border-width'
                defaultValue={headerStyleOptions.borderWidth.value.toString()}
                editable={editable}
                submit={onBorderChange}
              />
            </SectionColumn>
          </SectionRow>
        </>
      )}
    </div>
  );
};

export default ScreenStylingSection;
